import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useWeekOverview } from '../hooks/useWeekOverview';
import type { WeekNavigation } from '../types/weekOverview';
import { WeekOverviewHeader } from '../components/WeekOverview/WeekOverviewHeader/WeekOverviewHeader';
import { WeekKpiCards } from '../components/WeekOverview/WeekKpiCards/WeekKpiCards';
import { WeekHeatmap } from '../components/WeekOverview/WeekHeatmap/WeekHeatmap';
import { WeekEmptyState } from '../components/WeekOverview/WeekEmptyState/WeekEmptyState';
import { IndividualCalendarModal } from '../components/WeekOverview/IndividualCalendarModal/IndividualCalendarModal';
import { UnscheduledInterventionsModal } from '../components/WeekOverview/UnscheduledInterventionsModal/UnscheduledInterventionsModal';
import styles from './WeekOverviewPage.module.css';

export const WeekOverviewPage = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const {
    weekData,
    isLoading,
    hasLoadFailed,
    filters,
    setFilters,
    goToPreviousWeek,
    goToNextWeek,
    goToToday,
    goToDate,
    isViewingCurrentWeek
  } = useWeekOverview(locationId);

  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [isUnscheduledModalOpen, setIsUnscheduledModalOpen] = useState(false);

  // Selecting a staff row in the heatmap opens the calendar pre-filtered to that
  // person, so the planner jumps straight from the capacity signal to the detail.
  const handleStaffSelect = useCallback(
    (staffId: string) => {
      setFilters({ staffId, roomId: null, patientId: null });
      setIsCalendarModalOpen(true);
    },
    [setFilters]
  );

  // Opening always starts from a clean slate — filters left over from an
  // earlier visit would silently pre-filter the calendar.
  const handleOpenCalendar = useCallback(() => {
    setFilters({ staffId: null, roomId: null, patientId: null });
    setIsCalendarModalOpen(true);
  }, [setFilters]);

  const handleCloseCalendar = useCallback(() => {
    setIsCalendarModalOpen(false);
  }, []);

  // Memoized because the modal derives selection-clearing handlers from it.
  const weekNavigation = useMemo<WeekNavigation>(
    () => ({
      isViewingCurrentWeek,
      onPreviousWeek: goToPreviousWeek,
      onNextWeek: goToNextWeek,
      onGoToToday: goToToday
    }),
    [isViewingCurrentWeek, goToPreviousWeek, goToNextWeek, goToToday]
  );

  const handleOpenUnscheduled = useCallback(() => {
    setIsUnscheduledModalOpen(true);
  }, []);

  const handleCloseUnscheduled = useCallback(() => {
    setIsUnscheduledModalOpen(false);
  }, []);

  if (!weekData) return null;

  // While a week's result is loading, the header (rendered from the shell)
  // stays interactive but the body shows neither stale data nor a misleading
  // "not planned yet" flash. A failed fetch is suppressed for the same reason:
  // the error dialog already says what happened, and the week's real state is
  // unknown rather than empty.
  const showEmptyState = !weekData.isPlanned && !isLoading && !hasLoadFailed;

  return (
    <main className={styles.weekOverview}>
      <WeekOverviewHeader
        weekNumber={weekData.weekNumber}
        weekStartDate={weekData.weekStartDate}
        weekEndDate={weekData.weekEndDate}
        locationName={weekData.locationName}
        lastModifiedDate={weekData.isPlanned ? weekData.lastModifiedDate : ''}
        isViewingCurrentWeek={isViewingCurrentWeek}
        onPreviousWeek={goToPreviousWeek}
        onNextWeek={goToNextWeek}
        onGoToToday={goToToday}
        onJumpToDate={goToDate}
        onOpenCalendar={handleOpenCalendar}
        isCalendarDisabled={!weekData.isPlanned}
        unscheduledCount={weekData.isPlanned ? weekData.unscheduledInterventions.length : 0}
        onOpenUnscheduled={handleOpenUnscheduled}
      />
      {weekData.isPlanned && (
        <>
          <WeekKpiCards kpis={weekData.kpis} />
          <div className={styles.section}>
            <WeekHeatmap weekData={weekData} onStaffSelect={handleStaffSelect} />
          </div>
        </>
      )}
      {showEmptyState && (
        <div className={styles.section}>
          <WeekEmptyState onGoToCurrentWeek={goToToday} />
        </div>
      )}
      {isUnscheduledModalOpen && weekData.isPlanned && (
        <UnscheduledInterventionsModal weekData={weekData} onClose={handleCloseUnscheduled} />
      )}
      {isCalendarModalOpen && (
        <IndividualCalendarModal
          weekData={weekData}
          filters={filters}
          onFiltersChange={setFilters}
          onClose={handleCloseCalendar}
          navigation={weekNavigation}
        />
      )}
    </main>
  );
};
